import { useState } from 'react';
import { buildGenerationRequest } from '@/hooks/generation/shared/build-generation-request';
import { buildGenerationResult } from '@/hooks/generation/shared/build-generation-result';
import { parseGenerationStream } from '@/hooks/generation/shared/parse-generation-stream';
import { reportGenerationClientFailure } from '@/hooks/generation/shared/report-generation-client-failure';
import { normalizeGenerationError } from '@/utils/generationErrors';
import { resolveGuestMediaInputs } from '@/lib/auth/guest-media-handoff-client';

const isHandledGenerationError = (errorOrMessage, status) => {
  if (typeof status === 'number' && status >= 400 && status < 500) {
    return true;
  }

  const normalized = String(
    errorOrMessage?.message || errorOrMessage || ''
  ).toLowerCase();

  if (!normalized) return false;

  return (
    normalized.includes('verification failed') ||
    normalized.includes('please complete verification') ||
    normalized.includes('turnstile') ||
    normalized.includes('upgrade required') ||
    normalized.includes('please upgrade') ||
    normalized.includes('too many requests') ||
    normalized.includes('concurrent tasks') ||
    normalized.includes('video_duration') ||
    normalized.includes('input video duration') ||
    normalized.includes('reference video too long') ||
    normalized.includes('source video too long') ||
    normalized.includes('supports at most') ||
    (normalized.includes('must be') && normalized.includes('or shorter') && normalized.includes('received'))
  );
};

export function useGenerationRunner() {
  const [asset, setAsset] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [additionalInfo, setAdditionalInfo] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null); // 新增：Data Preview URL
  
  const runGeneration = async (params) => {
    let requestData = null;
    let apiEndpoint = null;
    let failureReported = false;

    try {
      setIsLoading(true);
      setError(null);
      setAdditionalInfo(null); // 重置额外信息
      setPreviewUrl(null); // 重置 Preview URL
      const {
        requestData: nextRequestData,
        sanitizedRequest,
        apiEndpoint: nextApiEndpoint,
      } = buildGenerationRequest(params);
      requestData = await resolveGuestMediaInputs(nextRequestData);
      apiEndpoint = nextApiEndpoint;
      console.log('Frontend sending request:', sanitizedRequest);

      console.log(`Using API endpoint: ${apiEndpoint}`);

      // Send request to Next.js API route
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (response.status >= 500) {
          console.error('API error response:', errorText);
        } else {
          console.warn('API returned handled error response:', errorText);
        }
        let errorPayload = {
          code: null,
          message: 'Request failed',
        };
        try {
          const parsed = JSON.parse(errorText);
          errorPayload = parsed?.error && typeof parsed.error === 'object'
            ? parsed.error
            : {
                code: parsed?.errorCode || parsed?.code || null,
                message: parsed?.message || parsed?.error || errorPayload.message,
              };
        } catch {
          // ignore
        }
        const normalizedError = normalizeGenerationError(errorPayload, {
          fallbackCode: errorPayload.code || 'unknown',
          fallbackMessage: errorPayload.message || 'Request failed',
        });
        failureReported = true;
        void reportGenerationClientFailure({
          apiEndpoint,
          requestData,
          stage: 'http_non_ok',
          responseStatus: response.status,
          responseText: errorText,
          responseContentType: response.headers.get('content-type') || '',
          normalizedError,
          rawError: {
            message: errorPayload.message || null,
            rawMessage: errorText || null,
          },
        });
        const err = new Error(normalizedError.message);
        err.code = normalizedError.code;
        err.details = normalizedError;
        err.rawMessage = normalizedError.rawMessage || errorPayload.message || null;
        err.status = response.status;
        throw err;
      }

      const {
        finalData,
        error: errorDetail,
        errorMessage,
        errorCode,
      } = await parseGenerationStream(response, {
        onAdditionalInfoPatch: (patch) => {
          setAdditionalInfo((prev) => ({
            ...prev,
            ...patch,
          }));
        },
        onPreviewUrl: (nextPreviewUrl) => {
          setPreviewUrl(nextPreviewUrl);
        },
      });

      // 优先检查是否有错误消息（来自后端）
      if (errorMessage && !finalData) {
        const normalizedError = normalizeGenerationError(
          errorDetail || {
            code: errorCode || null,
            message: errorMessage,
          },
          {
            fallbackCode: errorCode || 'unknown',
            fallbackMessage: errorMessage,
          }
        );
        failureReported = true;
        void reportGenerationClientFailure({
          apiEndpoint,
          requestData,
          stage: 'stream_backend_error',
          responseStatus: response.status,
          responseContentType: response.headers.get('content-type') || '',
          normalizedError,
          rawError: {
            message: errorMessage || null,
            rawMessage: normalizedError.rawMessage || errorMessage || null,
          },
        });
        const err = new Error(normalizedError.message);
        err.code = normalizedError.code;
        err.details = normalizedError;
        err.rawMessage = normalizedError.rawMessage || errorMessage || null;
        throw err;
      }

      // 如果没有错误但也没有完整数据，抛出通用错误
      if (!finalData) {
        failureReported = true;
        void reportGenerationClientFailure({
          apiEndpoint,
          requestData,
          stage: 'stream_no_final_data',
          normalizedError: {
            code: 'unknown',
            message: 'No complete response received',
            rawMessage: 'No complete response received',
          },
          rawError: {
            message: 'No complete response received',
            rawMessage: 'No complete response received',
          },
        });
        throw new Error('No complete response received');
      }

      const result = buildGenerationResult(finalData);
      if (result.assetUrl) {
        setAsset(result.assetUrl);
      }
      return result;
    } catch (err) {
      const normalizedMessage = String(err?.message || '');
      const isLikelyClientRequestLayerFailure =
        !failureReported &&
        (
          !err?.status
          || normalizedMessage.toLowerCase().includes('request failed')
          || normalizedMessage.toLowerCase().includes('failed to fetch')
          || normalizedMessage.toLowerCase().includes('load failed')
          || normalizedMessage.toLowerCase().includes('networkerror')
        );

      if (isLikelyClientRequestLayerFailure) {
        failureReported = true;
        void reportGenerationClientFailure({
          apiEndpoint,
          requestData,
          stage: 'fetch_or_client_exception',
          responseStatus: Number.isFinite(Number(err?.status)) ? Number(err.status) : 0,
          normalizedError: err?.details || normalizeGenerationError(err, {
            fallbackCode: err?.code || 'unknown',
            fallbackMessage: err?.message || 'Request failed',
          }),
          rawError: {
            message: err?.message || null,
            rawMessage: err?.rawMessage || err?.details?.rawMessage || null,
          },
        });
      }

      if (isHandledGenerationError(err, err?.status)) {
        console.warn('Generation handled error:', err?.message || err);
      } else {
        console.error('Generation error:', err);
      }
      setError(err.message);
      throw err; // Re-throw error so caller can catch it
    } finally {
      setIsLoading(false);
    }
  };
  
  const clearAsset = () => setAsset(null);
  
  return {
    asset,
    isLoading,
    error,
    additionalInfo,
    previewUrl,
    runGeneration,
    clearAsset,
  };
}
